import { type FormEvent, type ReactNode, useId, useRef, useState } from 'react';

import { css } from '@compiled/react';
import { FormattedMessage, useIntl } from 'react-intl';

import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';

import { Button, type ButtonVariant } from '@/libraries/design/components/button/src/button';
import { wacToken } from '@/libraries/design/foundations';
import { useLocalizedHref } from '@/libraries/web-framework/src/localization/use-localized-href';
import { TypographyStyles } from '@/libraries/wpl/foundations/src/typography-styles';

import { useConsentConfig } from './hooks/use-consent-config';
import { useSubmitEmailSubscription } from './hooks/use-submit-email-subscription';
import { buildConsentTexts } from './messages/build-consent-texts';
import { emailSubscriptionFormMessages } from './messages/form-messages';
import { EmailSubscriptionStatusMessage, getStatusMessage } from './messages/status-message';

export type EmailSubscriptionFormOrientation = 'horizontal' | 'vertical';

type EmailSubscriptionFormButtonVariant = Exclude<ButtonVariant, 'secondary'>;

export interface UniformEmailSubscriptionFormProps {
	formId: string;
	showPrivacyAgreement?: boolean;
	placeholderText?: string;
	orientation?: EmailSubscriptionFormOrientation;
	buttonVariant?: EmailSubscriptionFormButtonVariant;
}

const UniformEmailSubscriptionForm = (props: UniformEmailSubscriptionFormProps) => {
	const {
		formId,
		showPrivacyAgreement = false,
		placeholderText,
		orientation = 'horizontal',
		buttonVariant = 'primary',
	} = props;

	const intl = useIntl();
	const privacyPolicyHref = useLocalizedHref('/legal/privacy-policy');
	const consentConfig = useConsentConfig();
	const productFamilyHref = useLocalizedHref('/legal/privacy-policy/product-family');
	const partnersHref = useLocalizedHref('/partners');
	const inputRef = useRef<HTMLInputElement>(null);
	const [email, setEmail] = useState('');
	const [isEmailInvalid, setIsEmailInvalid] = useState(false);
	const [hasMarketingOptIn, setHasMarketingOptIn] = useState(false);
	const inputId = `${useId().replace(/:/g, '')}-email-subscription`;
	const errorId = `${inputId}-error`;

	const showMarketingOptIn = Boolean(consentConfig.localeRequiresMarketingCommunicationOptIn);
	const label = intl.formatMessage(emailSubscriptionFormMessages.emailLabel);
	const placeholder =
		placeholderText?.trim() || intl.formatMessage(emailSubscriptionFormMessages.emailPlaceholder);

	const { consentLabelText, privacyPolicyText } = buildConsentTexts({
		intl,
		showMarketingOptIn,
		showPrivacyAgreement,
		privacyPolicyHref,
		productFamilyHref,
		partnersHref,
	});

	const { status, submit, reset } = useSubmitEmailSubscription({
		formId,
		consentConfig,
		consentLabelText,
		privacyPolicyText,
	});
	const isPending = status === 'pending';
	const isSuccess = status === 'success';
	const statusMessage = getStatusMessage({ intl, isEmailInvalid, status });

	const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		if (isPending) {
			return;
		}

		if (!isValidEmail(email)) {
			setIsEmailInvalid(true);
			inputRef.current?.focus();
			return;
		}

		setIsEmailInvalid(false);
		// Locales that don't require an explicit opt-in consent implicitly.
		void submit({ email, hasMarketingOptIn: showMarketingOptIn ? hasMarketingOptIn : true });
	};

	return (
		<TypographyStyles>
			<form
				css={[formCss, orientation === 'horizontal' && horizontalFormCss]}
				data-testid="uniform-email-subscription-form"
				noValidate
				onSubmit={handleSubmit}
			>
				<label css={visuallyHiddenCss} htmlFor={inputId}>
					{label}
				</label>
				<input
					aria-describedby={isEmailInvalid ? errorId : undefined}
					aria-invalid={isEmailInvalid}
					autoComplete="email"
					css={inputCss}
					id={inputId}
					name="email"
					onChange={(event) => {
						setEmail(event.target.value);
						setIsEmailInvalid(false);
						reset();
					}}
					placeholder={placeholder}
					ref={inputRef}
					type="email"
					value={email}
				/>
				{statusMessage && (
					<EmailSubscriptionStatusMessage errorId={errorId} message={statusMessage} />
				)}
				{showPrivacyAgreement && !isSuccess && (
					<span css={[fullRowCss, messageCss]}>
						<FormattedMessage
							{...emailSubscriptionFormMessages.privacyAgreement}
							values={{
								// eslint-disable-next-line react/no-unstable-nested-components
								link: (chunks: ReactNode) => (
									<a css={privacyLinkCss} href={privacyPolicyHref}>
										{chunks}
									</a>
								),
							}}
						/>
					</span>
				)}
				{showMarketingOptIn && !isSuccess && (
					<label css={[fullRowCss, marketingOptInLabelCss]}>
						<input
							checked={hasMarketingOptIn}
							css={checkboxCss}
							name="marketingOptIn"
							onChange={(event) => setHasMarketingOptIn(event.target.checked)}
							type="checkbox"
						/>
						<span>
							<FormattedMessage
								{...emailSubscriptionFormMessages.marketingOptIn}
								values={{
									// eslint-disable-next-line react/no-unstable-nested-components
									atlassianLink: (chunks: ReactNode) => (
										<a css={privacyLinkCss} href={productFamilyHref}>
											{chunks}
										</a>
									),
									// eslint-disable-next-line react/no-unstable-nested-components
									partnersLink: (chunks: ReactNode) => (
										<a css={privacyLinkCss} href={partnersHref}>
											{chunks}
										</a>
									),
								}}
							/>
						</span>
					</label>
				)}
				<Button
					css={[submitCss, orientation === 'horizontal' && horizontalSubmitCss]}
					isDisabled={isPending}
					isLoading={isPending}
					size="large"
					type="submit"
					variant={buttonVariant}
				>
					{intl.formatMessage(emailSubscriptionFormMessages.subscribeButton)}
				</Button>
			</form>
		</TypographyStyles>
	);
};

export default UniformEmailSubscriptionForm;

const EMAIL_PATTERN =
	/^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i;

const isValidEmail = (email: string): boolean => EMAIL_PATTERN.test(email.trim());

// The submit button is last in the DOM so it stays at the bottom whenever the
// form is stacked; the horizontal layout lifts it back beside the input.
const formCss = css({
	color: wacToken('color.primary.black'),
	display: 'grid',
	fontFamily: wacToken('font.charlie.text'),
	gap: token('space.150'),
	gridTemplateColumns: '1fr',
	width: '100%',
});

const horizontalFormCss = css({
	[media.above.sm]: {
		gridTemplateColumns: '1fr auto',
	},
});

const fullRowCss = css({
	gridColumn: '1 / -1',
});

const submitCss = css({
	gridColumn: '1 / -1',
});

const horizontalSubmitCss = css({
	[media.above.sm]: {
		gridColumn: '2',
		gridRow: '1',
	},
});

const inputCss = css({
	appearance: 'none',
	backgroundColor: wacToken('color.neutral.light0'),
	border: `1px solid ${wacToken('color.neutral.light600')}`,
	borderRadius: wacToken('corner.radius.card'),
	boxSizing: 'border-box',
	color: wacToken('color.primary.black'),
	flex: '1 1 auto',
	fontFamily: wacToken('font.charlie.text'),
	fontSize: '1rem',
	lineHeight: '1.5rem',
	margin: 0,
	minHeight: token('space.500'),
	minWidth: 0,
	padding: `0 ${token('space.150')}`,
	'&:focus': {
		borderColor: wacToken('color.primary.blue'),
		boxShadow: `inset 0 0 0 1px ${wacToken('color.primary.blue')}`,
		outline: 'none',
	},
	'&[aria-invalid="true"]': {
		borderColor: wacToken('color.secondary.red'),
		boxShadow: `inset 0 0 0 1px ${wacToken('color.secondary.red')}`,
	},
});

// Rendered on spans rather than paragraphs: TypographyStyles targets `p` as a
// descendant, which outranks a single class and would reset size and colour.
const messageCss = css({
	display: 'block',
	fontSize: '0.875rem',
	lineHeight: '1.25rem',
	margin: 0,
});

const privacyLinkCss = css({
	color: wacToken('color.primary.blue'),
});

const marketingOptInLabelCss = css({
	alignItems: 'flex-start',
	display: 'flex',
	fontSize: '0.875rem',
	gap: token('space.100'),
	lineHeight: '1.25rem',
});

const checkboxCss = css({
	accentColor: wacToken('color.primary.blue'),
	flex: '0 0 auto',
	height: token('space.200'),
	margin: `${token('space.025')} 0 0`,
	width: token('space.200'),
});

const visuallyHiddenCss = css({
	height: '1px',
	insetInlineStart: '-10000px',
	overflow: 'hidden',
	position: 'absolute',
	width: '1px',
});
